import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './authContext';
import supabase from '../supabaseClient';
import { getVirtualBackgroundProcessor } from '../components/MediaSettings/VirtualBackgroundProcessor';

const MediaSettingsContext = createContext(null);

const STORAGE_KEY = 'virtulingu_media_settings';

const DEFAULT_SETTINGS = {
  selectedMicId: null,
  selectedSpeakerId: null,
  selectedCameraId: null,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  vbMode: 'none',
  vbBlurStrength: 8,
  vbPresetId: null,
  vbAssetId: null,
  vbImageFit: 'cover',
};

export function MediaSettingsProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const [devices, setDevices] = useState({ microphones: [], speakers: [], cameras: [] });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('microphone');
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [userAssets, setUserAssets] = useState([]);
  const isSyncingRef = useRef(false);

  // Enumerate devices
  const refreshDevices = useCallback(async () => {
    try {
      // Request permission first (needed to get device labels)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        stream.getTracks().forEach(t => t.stop());
      } catch {
        // Permission denied or no devices - continue anyway
      }

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        microphones: allDevices.filter(d => d.kind === 'audioinput'),
        speakers: allDevices.filter(d => d.kind === 'audiooutput'),
        cameras: allDevices.filter(d => d.kind === 'videoinput'),
      });
    } catch (err) {
      console.error('[MediaSettings] Failed to enumerate devices:', err);
    }
  }, []);

  // Load devices on mount
  useEffect(() => {
    refreshDevices();

    // Listen for device changes
    const handleDeviceChange = () => refreshDevices();
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshDevices]);

  // Load settings from Supabase on login
  useEffect(() => {
    if (!user?.id) return;

    const loadFromSupabase = async () => {
      try {
        const { data, error } = await supabase
          .from('media_settings')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('[MediaSettings] Failed to load from Supabase:', error);
          return;
        }

        if (data) {
          const loaded = {
            selectedMicId: data.selected_mic_id,
            selectedSpeakerId: data.selected_speaker_id,
            selectedCameraId: data.selected_camera_id,
            echoCancellation: data.echo_cancellation,
            noiseSuppression: data.noise_suppression,
            autoGainControl: data.auto_gain_control,
            vbMode: data.vb_mode,
            vbBlurStrength: data.vb_blur_strength,
            vbPresetId: data.vb_preset_id,
            vbAssetId: data.vb_asset_id,
            vbImageFit: data.vb_image_fit,
          };
          setSettings(prev => ({ ...prev, ...loaded }));
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...DEFAULT_SETTINGS, ...loaded }));
        }
      } catch (err) {
        console.error('[MediaSettings] Error loading settings:', err);
      }
    };

    loadFromSupabase();
  }, [user?.id]);

  // Load background presets
  useEffect(() => {
    const loadPresets = async () => {
      const { data } = await supabase
        .from('media_background_presets')
        .select('*')
        .eq('is_active', true)
        .order('name');
      setPresets(data || []);
    };
    loadPresets();
  }, []);

  // Load user's custom background assets
  useEffect(() => {
    if (!user?.id) return;

    const loadUserAssets = async () => {
      const { data } = await supabase
        .from('media_background_assets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setUserAssets(data || []);
    };
    loadUserAssets();
  }, [user?.id]);

  // Save settings (local + Supabase)
  const saveSettings = useCallback(async (newSettings) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));

    if (!user?.id || isSyncingRef.current) return;

    isSyncingRef.current = true;
    try {
      const payload = {
        user_id: user.id,
        selected_mic_id: merged.selectedMicId,
        selected_speaker_id: merged.selectedSpeakerId,
        selected_camera_id: merged.selectedCameraId,
        echo_cancellation: merged.echoCancellation,
        noise_suppression: merged.noiseSuppression,
        auto_gain_control: merged.autoGainControl,
        vb_mode: merged.vbMode,
        vb_blur_strength: merged.vbBlurStrength,
        vb_preset_id: merged.vbPresetId,
        vb_asset_id: merged.vbAssetId,
        vb_image_fit: merged.vbImageFit,
      };

      const { error } = await supabase
        .from('media_settings')
        .upsert(payload, { onConflict: 'user_id' });

      if (error) {
        console.error('[MediaSettings] Failed to save to Supabase:', error);
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [settings, user?.id]);

  // Upload custom background image
  const uploadBackgroundImage = useCallback(async (file) => {
    if (!user?.id) return null;

    const ext = file.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const storagePath = `${user.id}/${fileName}`;

    setLoading(true);
    try {
      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('media-backgrounds')
        .upload(storagePath, file, { upsert: false });

      if (uploadError) {
        console.error('[MediaSettings] Upload failed:', uploadError);
        return null;
      }

      // Create metadata record
      const { data: asset, error: insertError } = await supabase
        .from('media_background_assets')
        .insert({
          user_id: user.id,
          name: file.name,
          storage_bucket: 'media-backgrounds',
          storage_path: storagePath,
          mime_type: file.type,
          byte_size: file.size,
        })
        .select()
        .single();

      if (insertError) {
        console.error('[MediaSettings] Failed to create asset record:', insertError);
        return null;
      }

      setUserAssets(prev => [asset, ...prev]);
      return asset;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Delete custom background image
  const deleteBackgroundImage = useCallback(async (assetId) => {
    if (!user?.id) return false;

    const asset = userAssets.find(a => a.id === assetId);
    if (!asset) return false;

    setLoading(true);
    try {
      // Delete from storage
      await supabase.storage
        .from('media-backgrounds')
        .remove([asset.storage_path]);

      // Delete metadata
      await supabase
        .from('media_background_assets')
        .delete()
        .eq('id', assetId);

      setUserAssets(prev => prev.filter(a => a.id !== assetId));

      // Clear selection if this was the active asset
      if (settings.vbAssetId === assetId) {
        saveSettings({ vbAssetId: null, vbMode: 'none' });
      }

      return true;
    } finally {
      setLoading(false);
    }
  }, [user?.id, userAssets, settings.vbAssetId, saveSettings]);

  // Get signed URL for a background asset
  const getAssetUrl = useCallback(async (storagePath) => {
    const { data } = await supabase.storage
      .from('media-backgrounds')
      .createSignedUrl(storagePath, 60 * 60); // 1 hour
    return data?.signedUrl || null;
  }, []);

  // Build getUserMedia constraints using current settings
  const getMediaConstraints = useCallback(({ mode = 'video' } = {}) => {
    const audioConstraints = {
      echoCancellation: settings.echoCancellation,
      noiseSuppression: settings.noiseSuppression,
      autoGainControl: settings.autoGainControl,
    };

    if (settings.selectedMicId) {
      audioConstraints.deviceId = { exact: settings.selectedMicId };
    }

    const constraints = {
      audio: audioConstraints,
      video: false,
    };

    if (mode === 'video') {
      const videoConstraints = {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };
      if (settings.selectedCameraId) {
        videoConstraints.deviceId = { exact: settings.selectedCameraId };
      }
      constraints.video = videoConstraints;
    }

    return constraints;
  }, [settings]);

  // Apply virtual background to a video track
  const applyVirtualBackground = useCallback(async (videoTrack) => {
    if (!videoTrack || settings.vbMode === 'none') {
      return videoTrack;
    }

    try {
      const processor = getVirtualBackgroundProcessor();
      
      // Get background image URL if mode is 'image'
      let backgroundImageUrl = null;
      if (settings.vbMode === 'image') {
        if (settings.vbPresetId) {
          const preset = presets.find(p => p.id === settings.vbPresetId);
          backgroundImageUrl = preset?.image_url;
        } else if (settings.vbAssetId) {
          const asset = userAssets.find(a => a.id === settings.vbAssetId);
          if (asset) {
            backgroundImageUrl = await getAssetUrl(asset.storage_path);
          }
        }
      }

      const processedTrack = await processor.start(videoTrack, {
        mode: settings.vbMode,
        blurStrength: settings.vbBlurStrength,
        backgroundImageUrl,
      });

      return processedTrack;
    } catch (err) {
      console.error('[MediaSettings] Failed to apply virtual background:', err);
      return videoTrack; // Return original on error
    }
  }, [settings, presets, userAssets, getAssetUrl]);

  // Stop virtual background processing
  const stopVirtualBackground = useCallback(() => {
    try {
      const processor = getVirtualBackgroundProcessor();
      processor.stop();
    } catch (err) {
      console.error('[MediaSettings] Failed to stop virtual background:', err);
    }
  }, []);

  // Open modal (optionally to a specific tab)
  const openMediaSettings = useCallback((tab = 'microphone') => {
    setActiveTab(tab);
    setIsModalOpen(true);
    refreshDevices();
  }, [refreshDevices]);

  const closeMediaSettings = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const value = {
    settings,
    saveSettings,
    devices,
    refreshDevices,
    presets,
    userAssets,
    uploadBackgroundImage,
    deleteBackgroundImage,
    getAssetUrl,
    getMediaConstraints,
    applyVirtualBackground,
    stopVirtualBackground,
    isModalOpen,
    openMediaSettings,
    closeMediaSettings,
    activeTab,
    setActiveTab,
    loading,
  };

  return (
    <MediaSettingsContext.Provider value={value}>
      {children}
    </MediaSettingsContext.Provider>
  );
}

export function useMediaSettings() {
  const context = useContext(MediaSettingsContext);
  if (!context) {
    throw new Error('useMediaSettings must be used within a MediaSettingsProvider');
  }
  return context;
}

export default MediaSettingsContext;
